import { useState } from "react";
import { v4 } from "uuid";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  BranchReference,
  BranchRole,
} from "@/services/backend/documents/types/branch-references-base";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { Offer } from "../../offer/types/offer";
import { useOmniContractCreateMutation } from "../api/contract-api";
import { ContractBaseDataSelect } from "./contract-base-data-select";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";
import {
  ErrValidationFailedContextProvider,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";

/**
 * ContractCreateButtonConfig configures the {@link ContractCreateButton}
 * per service kind. With selectBaseData, the user selects the base data
 * filling the new contract.
 */
export type ContractCreateButtonConfig = {
  selectBaseData: boolean;
};

/**
 * ContractCreateButton creates a contract referencing the offer, for its
 * process, company, primary branch, customer and first service object.
 * With config.selectBaseData, it opens a dialog to select the base data
 * filling the contract first. onCreated receives the id of the contract.
 */
export function ContractCreateButton({
  offer,
  config,
  onCreated,
  disabled,
}: {
  offer: Offer;
  config: ContractCreateButtonConfig;
  onCreated: (contractId: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [create, { isLoading, reset }] = useOmniContractCreateMutation();

  const branchId =
    offer.branches.find(
      (b: BranchReference) => b.branchRole === BranchRole.Primary,
    )?.branchId ?? "";

  const doCreate = (baseDataId?: string) => {
    if (isLoading) {
      return Promise.resolve();
    }
    return create({
      id: v4(),
      serviceKind: offer.serviceKind as GSMServiceKind,
      processId: offer.processId ?? NullUUID,
      companyId: offer.companyId,
      branchId,
      customerId: offer.customerId ?? NullUUID,
      serviceObjectId: offer.serviceObjects[0]?.serviceObjectId ?? NullUUID,
      offerId: offer.id,
      baseDataId,
    })
      .unwrap()
      .then((contract) => {
        reset();
        setOpen(false);
        onCreated(contract.id);
      })
      .catch((e) => {
        reset();
        toast(...parseRTKQueryErrorToSonner(e));
        // the dialog shows the field errors of the selection
        if (config.selectBaseData) {
          throw e;
        }
      });
  };

  const button = (
    <Button
      onClick={() => (config.selectBaseData ? setOpen(true) : void doCreate())}
      disabled={disabled || isLoading || branchId === ""}
      size="sm"
    >
      <Plus />
      {t("Vertrag")}
    </Button>
  );

  if (!config.selectBaseData) {
    return button;
  }

  return (
    <>
      {button}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          {/* the dialog creates a new entity, its errors stay its own */}
          <ErrValidationFailedContextProvider>
            <CreateForm
              offer={offer}
              isLoading={isLoading}
              onCreate={doCreate}
            />
          </ErrValidationFailedContextProvider>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CreateForm({
  offer,
  isLoading,
  onCreate,
}: {
  offer: Offer;
  isLoading: boolean;
  onCreate: (baseDataId?: string) => Promise<unknown>;
}) {
  const { write } = useErrValidationFailedContext();
  const [baseDataId, setBaseDataId] = useState<string>();

  return (
    <>
      <CommonDialogHeader
        title={t("Vertrag erstellen")}
        description={t(
          "Die Vorlage bestimmt die Vorgabewerte des Vertrags und seines Leistungsverzeichnisses.",
        )}
      />
      <ContractBaseDataSelect
        companyId={offer.companyId}
        serviceKind={offer.serviceKind}
        value={baseDataId}
        onBaseDataChange={(b) => setBaseDataId(b?.id)}
        fieldName="baseDataId"
      />
      <CommonDialogFooter
        isLoading={isLoading}
        onClickAction={() => onCreate(baseDataId).catch(write)}
        actionLabel={t("Erstellen")}
      />
    </>
  );
}
